import { useEffect, useMemo, useState } from 'react';
import { FiCreditCard, FiRefreshCw } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ecommerceApi } from '../services/api';

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function getStatusBadgeClass(status) {
  if (status === 'trial' || status === 'live' || status === 'live_pending_invoice') {
    return 'bg-green-100 text-green-800';
  }
  if (status === 'past_due' || status === 'trial_expired') {
    return 'bg-yellow-100 text-yellow-800';
  }
  if (status === 'canceled') {
    return 'bg-red-100 text-red-800';
  }
  return 'bg-gray-100 text-gray-700';
}

export default function BillingSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [account, setAccount] = useState(null);

  const loadAccount = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await ecommerceApi.fetchAccountStatus();
      setAccount(data);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to load billing status.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log(
      '[TEMP] Stripe publishable key:',
      process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    );
    loadAccount();
  }, []);

  const status = account?.status || 'unknown';
  const isBlocked = Boolean(account?.is_blocked);
  const hasPaymentMethod = Boolean(account?.has_default_payment_method);
  const nextAction = account?.next_action || 'none';

  const nextActionLabel = useMemo(() => {
    if (nextAction === 'add_payment_method') return 'Add payment method';
    if (nextAction === 'resolve_payment') return 'Resolve payment issue';
    return 'None';
  }, [nextAction]);

  const ctaLabel = useMemo(() => {
    if (nextAction === 'add_payment_method') return 'Set Up Billing';
    if (nextAction === 'resolve_payment') return 'Update Payment Method';
    return 'Manage Billing Method';
  }, [nextAction]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center mb-2">
            <FiCreditCard className="w-6 h-6 text-gray-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
          </div>
          <p className="text-gray-600">View account trial/live status and billing readiness.</p>
        </div>
        <button
          onClick={loadAccount}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 disabled:opacity-50"
        >
          <FiRefreshCw className="mr-2" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-gray-600">Loading billing status...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Status</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(status)}`}>
                    {status}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Blocked</span>
                  <span className="text-gray-900 font-medium">{isBlocked ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Next Action</span>
                  <span className="text-gray-900 font-medium">{nextActionLabel}</span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Trial & Payment</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Trial Start</span>
                  <span className="text-gray-900 font-medium">{formatDate(account?.trial_started_at)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Trial End</span>
                  <span className="text-gray-900 font-medium">{formatDate(account?.trial_ends_at)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Default Payment Method</span>
                  <span className="text-gray-900 font-medium">{hasPaymentMethod ? 'On file' : 'Not on file'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Billing Actions</h2>
            <p className="text-gray-600 text-sm mb-4">
              Use this action to add or update your payment method in Distributely.
            </p>
            <button
              onClick={() => navigate('/settings/billing/setup')}
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition duration-200"
            >
              <FiCreditCard className="mr-2" />
              {ctaLabel}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
